import {
  BaseDropdownMenuHover,
  Button,
  Card,
  CountryOptionChildren,
  FacebookIcon,
  Footer,
  Header,
  HeaderAvatarMenuOptionDefault,
  HeaderNavLink,
  HeaderNavLinks,
  HeaderSearchBar,
  HeaderUserActions,
  InstagramIcon,
  MailIcon,
  SidebarMenuContent,
  SidebarMenuContentOption,
  SidebarNavOption,
  SidebarProduct,
  SidebarProductContainer,
  SideBarWithBackdrop,
  TwitterIcon,
} from "@bohiio/ui-components";
import { getWsLink } from "../utils/whatsappText";
import { useState } from "react";
import { BohiioMarketplaceLinks, ROUTES } from "../utils/links";
import { redirect, useNavigate } from "@remix-run/react";
import { UserInfo } from "../utils/profile/types";

export interface EnvValues {
  CDN_URL: string;
  SERVICE_PHONE_NUMBER: string;
  BOHIIO_MARKETPLACE_URL: string;
  INSTAGRAM_URL: string;
  FACEBOOK_URL: string;
  X_SOCIAL_MEDIA_URL: string;
  BOHIIO_SUPPORT_MAIL_ADDRESS: string;
}

interface NavbarProps {
  showSupport?: boolean;
  showNav?: boolean;
  showUser?: boolean;
  showSearchBar?: boolean;
}

interface UserActionProps {
  showCountrySelect?: boolean;
  showPublishProperties?: boolean;
  showNotifications?: boolean;
  showAvatarUser?: boolean;
  showLoginButton?: boolean;
}

interface BaseTemplateProps {
  children: React.ReactNode;
  env: EnvValues;
  navbarProps?: NavbarProps;
  userActionProps?: UserActionProps;
  userProfile?: UserInfo;
  links: BohiioMarketplaceLinks;
}

const WhatsappMessages = {
  generalQuestion: "Hola! Deseo conocer más sobre Bohiio.",
  priceRealStateRequestQuote:
    "Hola! Deseo solicitar una cotización para tasar un inmueble.",
  certifyRealStateRequestQuote:
    "Hola! Deseo solicitar una cotización para regularizar mi propiedad.",
  certifyRealStateInfo:
    "Hola! Deseo conocer más información sobre los servicios para regularizar mi propiedad.",
  verifyPropertyHelp: "Hola! Necesito asistencia para verificar una propiedad.",
  planServiceInformation:
    "Hola! Deseo conocer más información sobre los servicios y planes de Bohiio.",
  verificationCoupon:
    "Hola! Deseo obtener el cupón de descuento para mi primera verificación.",
  refereeInfoProgram:
    "Hola! Soy agente inmobiliario y deseo conocer más detalles del programa de referidos para generar ingresos con Bohiio.",
  agentWork:
    "Hola! Soy agente inmobiliario y deseo conocer más detalles para trabajar con Bohiio.",
  appraiserWork:
    "Hola! Soy tasador/a y deseo conocer más detalles para trabajar con Bohiio.",
  layerWork:
    "Hola! Soy abogado/a y deseo conocer más detalles para trabajar con Bohiio.",
  surveyorWork:
    "Hola! Soy agrimensor/a y deseo conocer más detalles para trabajar con Bohiio.",
  paymentsInfo:
    "Hola! Deseo conocer más información sobre las facilidades de pago para los procesos de regularización.",
  verificationPlansHighVolume:
    "Hola! Deseo conocer más información sobre los paquetes de verificaciones para altos volúmenes.",
};

export function BaseTemplate({
  children,
  env,
  navbarProps = {
    showNav: true,
    showSearchBar: false,
    showSupport: true,
    showUser: true,
  },
  userActionProps = {
    showAvatarUser: false,
    showCountrySelect: false,
    showLoginButton: true,
    showNotifications: false,
    showPublishProperties: false,
  },
  userProfile,
  links,
}: BaseTemplateProps) {
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();
  const [isLoadingSearchBar, setIsLoadingSearchBar] = useState(false);
  const [showSearchError, setShowSearchError] = useState(false);
  let h = HeaderAvatarMenuOptionDefault;

  delete h["incomes"];
  delete h["archived"];

  h["profile"].text = "Perfil";
  h["profile"].onClick = () =>
    window.open(links.profile(userProfile?.profile.username || ""), "_self");

  h["profiles"].text = "Configuracion";
  h["profiles"].onClick = () => window.open(links.configuration, "_self");

  h["plans"].text = "Planes";
  h["plans"].onClick = () => window.open(links.plans, "_self");

  h["favorites"].text = "Propiedades guardadas";
  h["favorites"].onClick = () => window.open(links.favorites, "_self");

  const {
    SERVICE_PHONE_NUMBER,
    BOHIIO_SUPPORT_MAIL_ADDRESS,
    FACEBOOK_URL,
    INSTAGRAM_URL,
    X_SOCIAL_MEDIA_URL,
  } = env;

  return (
    <>
      <div className="fixed top-0 left-0 px-7 md:px-2 py-[2.5px] w-full flex items-center h-16 z-[1000] bg-white">
        <Header
          showUser={true}
          showSupport={true}
          contentNav={
            <HeaderNavLinks>
              <HeaderNavLink>
                <BaseDropdownMenuHover
                  position="bottom"
                  menu={
                    <div style={{ paddingTop: "30px" }}>
                      <Card
                        style={{
                          width: "300px",
                          backgroundColor: "white",
                          padding: "10px",
                          color: "black",
                          fontWeight: "initial",
                        }}
                      >
                        <a href={links.verificatioRequest}>
                          <SidebarProduct title="Verificación de Inmuebles">
                            Obtén información legal y catastral actualizada de
                            la propiedad que deseas adquirir.
                          </SidebarProduct>
                        </a>
                        <hr className="my-4 mx-0" />
                        <a
                          href={getWsLink(
                            SERVICE_PHONE_NUMBER!,
                            WhatsappMessages.priceRealStateRequestQuote
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <SidebarProduct title="Tasaciones y Avalúos">
                            Determina el valor real de tu propiedad con nuestros
                            servicios de tasación especializados.
                          </SidebarProduct>
                        </a>
                        <hr className="my-4 mx-0" />
                        <a href="#update">
                          <SidebarProduct title="Procesos de titulación inmobiliaria">
                            Regularizamos el estatus de tu inmueble hasta
                            obtener el Certificado de Título, con opciones de
                            pago extendidas hasta 3 años, sin intereses ni
                            penalidades.
                          </SidebarProduct>
                        </a>
                      </Card>
                    </div>
                  }
                >
                  Productos
                </BaseDropdownMenuHover>
              </HeaderNavLink>
              <HeaderNavLink href={"#clients"}>
                Genera ingresos con Bohiio
              </HeaderNavLink>
              <HeaderNavLink href={links.plans}>Ver planes</HeaderNavLink>
              <HeaderNavLink href={links.base}>
                Marketplace Inmobiliario
              </HeaderNavLink>
            </HeaderNavLinks>
          }
          searchBar={
            <HeaderSearchBar
              searchBarProps={{
                onFail: async (reason) => {
                  const t = reason as string;
                  setShowSearchError(true);
                  return t;
                },
                onSearch: async (text) => {
                  setShowSearchError(false);
                  setIsLoadingSearchBar(true);
                  // await sleep(2000);
                  setIsLoadingSearchBar(false);
                  if (text.includes("error")) {
                    throw "Aqui hay un fallo";
                  }
                },
                errorChildren: <div>Aqui hubo un problema</div>,
                showErrorChildren: showSearchError,
                results: [],
                isLoading: isLoadingSearchBar,
                noResultsChildren: "No hay resultados",
                textPlaceholder: "Busca aqui lo que quieras",
              }}
            />
          }
          contentUser={
            <div>
              <HeaderUserActions
                {...userActionProps}
                publishPropertiesProps={{
                  isActive: false,
                  onClick: () => window.open(links.addProperty, "_self"),
                }}
                notificationsProps={{
                  isActive: false,
                  onClick: () => window.open(links.notifications, "_self"),
                  tipText:
                    userProfile?.newNotifications || 0 > 9
                      ? "+9"
                      : userProfile?.newNotifications || 0 > 0
                      ? userProfile?.newNotifications.toString()
                      : "",
                }}
                loginButtonProps={{
                  onClick: () =>
                    navigate("/login", {
                      state: { next: ROUTES.index },
                    }),
                  text: "Iniciar sesion",
                }}
                avatarUserProps={{
                  imageUrl: userProfile?.profile.profile.avatar || "",
                  menuOptions: h,
                  menuActions: {
                    close: {
                      id: "close",
                      onClick: () => navigate(ROUTES.logout, { replace: true }),
                      text: "Cerrar sesion",
                    },
                  },
                }}
                countrySelectProps={{
                  onSelect: (selected) =>
                    console.log("country selected: ", selected.id),
                  moreOptionsChildren: [
                    {
                      id: "ZZ",
                      data: {
                        text: "Todos los Paises",
                        countryName: "Todos los Paises",
                        flag: "🌎",
                      },
                      children: (
                        <CountryOptionChildren
                          countryValue={"Todos los Paises"}
                          flag={"🌎"}
                        />
                      ),
                    },
                  ],
                }}
              />
            </div>
          }
          onClickMenuButton={() => setShowSidebar((prev) => !prev)}
          {...navbarProps}
        />
      </div>
      <div
        className={`top-[4em] md:top-[52px] h-[calc(100vh_-_4em)] md:h-[calc(100vh_-_52px)] fixed z-[100] overflow-y-auto md:overflow-y-visible`}
      >
        <SideBarWithBackdrop
          sidebarProps={{
            className: `duration-[400ms] relative pt-5`,
            title: "Productos",
            products: (
              <SidebarProductContainer>
                <a href={links.verificatioRequest}>
                  <SidebarProduct title="Verificación de Inmuebles">
                    Obtén información legal y catastral actualizada de la
                    propiedad que deseas adquirir.
                  </SidebarProduct>
                </a>
                <hr />
                <a
                  href={getWsLink(
                    SERVICE_PHONE_NUMBER!,
                    WhatsappMessages.priceRealStateRequestQuote
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SidebarProduct title="Tasaciones y Avalúos">
                    Determina el valor real de tu propiedad con nuestros
                    servicios de tasación especializados.
                  </SidebarProduct>
                </a>
                <hr />
                <a href="#update">
                  <SidebarProduct title="Procesos de titulación inmobiliaria">
                    Regularizamos el estatus de tu inmueble hasta obtener el
                    Certificado de Título, con opciones de pago extendidas hasta
                    3 años, sin intereses ni penalidades.
                  </SidebarProduct>
                </a>
              </SidebarProductContainer>
            ),
            nav: (
              <div>
                <SidebarNavOption
                  optionContent={<span>Genera ingresos con Bohiio</span>}
                  menu={
                    <SidebarMenuContent>
                      <SidebarMenuContentOption
                        onClick={() => {
                          window.open(
                            getWsLink(
                              SERVICE_PHONE_NUMBER!,
                              WhatsappMessages.refereeInfoProgram
                            ),
                            "_blank"
                          );
                        }}
                      >
                        Agente Inmobiliario
                      </SidebarMenuContentOption>
                      <SidebarMenuContentOption
                        onClick={() => {
                          window.open(
                            getWsLink(
                              SERVICE_PHONE_NUMBER!,
                              WhatsappMessages.appraiserWork
                            ),
                            "_blank"
                          );
                        }}
                      >
                        Tasador/a
                      </SidebarMenuContentOption>
                      <SidebarMenuContentOption
                        onClick={() => {
                          window.open(
                            getWsLink(
                              SERVICE_PHONE_NUMBER!,
                              WhatsappMessages.layerWork
                            ),
                            "_blank"
                          );
                        }}
                      >
                        Abogado/a
                      </SidebarMenuContentOption>
                      <SidebarMenuContentOption
                        onClick={() => {
                          window.open(
                            getWsLink(
                              SERVICE_PHONE_NUMBER!,
                              WhatsappMessages.surveyorWork
                            ),
                            "_blank"
                          );
                        }}
                      >
                        Agrimensor/a
                      </SidebarMenuContentOption>
                    </SidebarMenuContent>
                  }
                />
                <a href={links.plans}>
                  <SidebarNavOption optionContent={<span>Ver planes</span>} />
                </a>
                <SidebarNavOption
                  optionContent={<span>Marketplace Inmobiliario</span>}
                  menu={
                    <SidebarMenuContent>
                      <a href={links.base}>
                        <SidebarMenuContentOption>
                          Feed
                        </SidebarMenuContentOption>
                      </a>
                      <a href={links.map}>
                        <SidebarMenuContentOption>
                          Mapa
                        </SidebarMenuContentOption>
                      </a>
                      <a href={links.tendency}>
                        <SidebarMenuContentOption>
                          Tendencias
                        </SidebarMenuContentOption>
                      </a>
                    </SidebarMenuContent>
                  }
                />
              </div>
            ),
            action: !userProfile && (
              <div>
                <p className="my-4 text-black text-sm">
                  ¡Descubre todo lo que nuestra plataforma tiene para ofrecerte!
                  Inicia sesión para acceder a funciones adicionales y
                  aprovechar al máximo tu experiencia como usuario.
                </p>
                <div style={{ width: "160px" }}>
                  <Button
                    onClick={() =>
                      navigate("/login", {
                        state: { next: ROUTES.index },
                      })
                    }
                    styleType="secondary"
                  >
                    Iniciar Sesion
                  </Button>
                </div>
              </div>
            ),
          }}
          isOpen={showSidebar}
          close={() => setShowSidebar(false)}
        />
      </div>
      <main className="app-main">{children}</main>
      <div className="app-footer">
        <Footer
          navLinksContent={[
            <a key="certify" href={links.verificatioRequest}>
              <span>Certifica tu propiedad</span>
            </a>,
            <a key="aboutUs" href={links.aboutUs}>
              <span>Quienes Somos</span>
            </a>,
            // <a key="questions" href={} >
            // <span key="questions">Preguntas frecuentes</span>,
            // </a>,
            <a key="privacy" href={links.privacy}>
              <span>Privacidad</span>
            </a>,
            <a key="terms" href={links.terms}>
              <span>Terminos</span>
            </a>,
            <a
              key="help"
              rel="noreferrer"
              target="_blank"
              href={getWsLink(
                SERVICE_PHONE_NUMBER!,
                WhatsappMessages.generalQuestion
              )}
            >
              <span>Ayuda</span>
            </a>,
            // <a key=""><span>Español</span></a>
          ]}
          socialContent={
            <nav>
              <ul>
                <li>
                  <a
                    href={`mailto:${BOHIIO_SUPPORT_MAIL_ADDRESS}?subject=Help`}
                  >
                    <MailIcon />
                  </a>
                </li>
                <li>
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FacebookIcon />
                  </a>
                </li>
                <li>
                  <a
                    href={X_SOCIAL_MEDIA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TwitterIcon />
                  </a>
                </li>
                <li>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <InstagramIcon />
                  </a>
                </li>
              </ul>
            </nav>
          }
        />
      </div>
    </>
  );
}
